import cx from 'classnames';
import {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Card} from 'components';
import {TRANSLATION_KEYS} from 'locale';
import {PreSessionStepStatus} from 'types';

import {preSessionFlowAnalyticsApi} from './api';
import {PreSessionFlowOutcome} from './types';
import type {PreSessionFlowRun, VersionLabel} from './types';
import {preSessionFlowAnalyticsUtils} from './utils';

const T_KEYS = TRANSLATION_KEYS.USER.ACTIVITY.PRE_SESSION_FLOW.RUN_DETAIL;

type BadgeTone = 'success' | 'alert' | 'warning' | 'primary' | 'neutral';

const TONE_CLASS: Record<BadgeTone, string> = {
    success: 'tw-bg-success-light tw-text-success',
    alert: 'tw-bg-alert-light tw-text-alert',
    warning: 'tw-bg-warning-light tw-text-warning',
    primary: 'tw-bg-primary-light tw-text-primary',
    neutral: 'tw-bg-layout tw-text-default-dimmed',
};

const OUTCOME_TONE: Record<string, BadgeTone> = {
    [PreSessionFlowOutcome.Escalated]: 'success',
    [PreSessionFlowOutcome.EscalatedPartial]: 'success',
    [PreSessionFlowOutcome.AbandonedByClient]: 'alert',
    [PreSessionFlowOutcome.AbandonedByTimeout]: 'alert',
    [PreSessionFlowOutcome.SystemEnded]: 'warning',
};

const STATUS_TONE: Record<string, BadgeTone> = {
    [PreSessionStepStatus.Done]: 'success',
    [PreSessionStepStatus.Failed]: 'alert',
    [PreSessionStepStatus.SkippedAfterRetries]: 'warning',
    [PreSessionStepStatus.InProgress]: 'primary',
    [PreSessionStepStatus.Pending]: 'neutral',
};

const Badge = ({label, tone}: {label: string; tone: BadgeTone}) => (
    <span className={cx('tw-inline-flex tw-px-2 tw-py-0.5 tw-rounded tw-text-xs tw-whitespace-nowrap', TONE_CLASS[tone])}>
        {label}
    </span>
);

type Props = {
    sessionId: string;
};

export const PreSessionFlowRunDetail = ({sessionId}: Props) => {
    const {t} = useTranslation();
    const [run, setRun] = useState<PreSessionFlowRun | null>(null);
    const [version, setVersion] = useState<VersionLabel | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        preSessionFlowAnalyticsApi.getRun(sessionId)
            .then(async (r) => {
                if (cancelled) {
                    return;
                }
                setRun(r);
                const labels = await preSessionFlowAnalyticsApi.getVersionsLabels([r.flowChecksum]);
                if (!cancelled) {
                    setVersion(labels[0] || null);
                }
            })
            .catch(() => setError(t(T_KEYS.NOT_FOUND)))
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [sessionId, t]);

    const stepNameById = useMemo(() => {
        const map = new Map<string, string>();
        if (version) {
            for (const s of version.steps) {
                map.set(s.stepId, s.name);
            }
        }
        return map;
    }, [version]);

    if (loading) {
        return <Card><div className='tw-text-sm tw-text-default-dimmed'>{t(T_KEYS.LOADING)}</div></Card>;
    }
    if (error || !run) {
        return <Card><div className='tw-text-sm tw-text-alert'>{error || t(T_KEYS.NOT_FOUND)}</div></Card>;
    }

    return (
        <Card>
            <div className='tw-flex tw-flex-wrap tw-items-center tw-gap-x-6 tw-gap-y-2 tw-mb-4 tw-text-sm'>
                <div className='tw-flex tw-items-center tw-gap-2'>
                    <span className='tw-text-default-dimmed'>{t(T_KEYS.OUTCOME)}</span>
                    <Badge
                        label={t(T_KEYS.OUTCOME_VALUE[run.outcome]) || run.outcome}
                        tone={OUTCOME_TONE[run.outcome] || 'neutral'}
                    />
                </div>
                <div>
                    <span className='tw-text-default-dimmed'>
                        {t(T_KEYS.DURATION)}
                        {': '}
                    </span>
                    {preSessionFlowAnalyticsUtils.formatStageDuration(run.durationMs, '—')}
                </div>
                <div>
                    <span className='tw-text-default-dimmed'>
                        {t(T_KEYS.COMPLETED)}
                        {': '}
                    </span>
                    {run.completedSteps}
                    {'/'}
                    {run.totalSteps}
                </div>
            </div>

            <h4 className='tw-ts-h5-bold tw-mb-2'>{t(T_KEYS.STEPS)}</h4>
            <div className='tw-overflow-x-auto'>
                <table className='tw-w-full tw-text-sm'>
                    <thead>
                        <tr className='tw-text-default-dimmed tw-text-xs tw-text-left'>
                            <th className='tw-pb-2 tw-pr-3'>{'#'}</th>
                            <th className='tw-pb-2 tw-pr-3'>{t(T_KEYS.NAME)}</th>
                            <th className='tw-pb-2 tw-pr-3'>{t(T_KEYS.TYPE)}</th>
                            <th className='tw-pb-2 tw-pr-3'>{t(T_KEYS.STATUS)}</th>
                            <th className='tw-pb-2 tw-pr-3 tw-text-right tw-whitespace-nowrap'>{t(T_KEYS.DURATION)}</th>
                            <th className='tw-pb-2 tw-text-right tw-whitespace-nowrap'>{t(T_KEYS.ATTEMPTS)}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {run.steps.map((s) => (
                            <tr key={s.stepId} className='tw-border-t tw-border-default'>
                                <td className='tw-py-2 tw-pr-3 tw-text-default-dimmed'>{s.stepIndex + 1}</td>
                                <td className='tw-py-2 tw-pr-3'>{stepNameById.get(s.stepId) || s.stepId.slice(0, 8)}</td>
                                <td className='tw-py-2 tw-pr-3 tw-text-default-dimmed'>{s.type}</td>
                                <td className='tw-py-2 tw-pr-3'>
                                    <Badge
                                        label={t(T_KEYS.STATUS_VALUE[s.status]) || s.status}
                                        tone={STATUS_TONE[s.status] || 'neutral'}
                                    />
                                </td>
                                <td className='tw-py-2 tw-pr-3 tw-text-right tw-tabular-nums'>
                                    {preSessionFlowAnalyticsUtils.formatStageDuration(s.durationMs, '—')}
                                </td>
                                <td className='tw-py-2 tw-text-right tw-tabular-nums'>{s.attempts}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </Card>
    );
};
